import moment from "moment";
import * as Icon from "react-feather";

import WComponent from "../../common/WComponent";
import EngagementService from "../../../services/EngagementService";
import { rrulestr } from "rrule";

import "./recurring_eng_schedule.css";
import SearchInput from "../../common/search_input/SearchInput";

class RecurringEngagementSchedule extends WComponent {
	constructor(props) {
		super(props);
		this.state = {
			loading: true,
			recurringEngagements: [],
			expandedId: null,
			search: "",
		};
	}

	componentDidMount = async () => {
		await this.fetchRecurringEngagements();
	};

	fetchRecurringEngagements = async () => {
		try {
			let response = await EngagementService.listRecurringEngagements();
			if (response) {
				this.setState({
					recurringEngagements: response.data || [],
					loading: false,
				});
			} else {
				this.setState({ loading: false });
			}
		} catch (error) {
			console.error("Failed to fetch recurring engagements", error);
			this.setState({ loading: false });
		}
	};

	onBack = () => {
		const { history } = this.props;
		if (history && history.goBack) {
			history.goBack();
		}
	};

	toggleExpand = (id) => {
		this.setState((prev) => ({
			expandedId: prev.expandedId === id ? null : id,
		}));
	};

	getNextOccurrences = (rruleString, maxCount = 5) => {
		if (!rruleString) return [];

		try {
			const rule = rrulestr(rruleString);
			const dates = [];
			let cursor = rule.after(new Date(), true); 

			while (cursor && dates.length < maxCount) {
				dates.push(cursor);
				cursor = rule.after(cursor, false);
			}

			return dates.map(
				(d) => moment.utc(d).format("ddd, MMM D, YYYY"), // e.g. "Wed, Nov 19, 2025"
			);
		} catch (err) {
			console.error("Unable to parse rrule:", rruleString, err);
			return [];
		}
	};

	getSnapshotName = (rec) => {
		let snapshotName = "Untitled Engagement";
		try {
			if (rec.engagement_snapshot) {
				const snapshot = JSON.parse(rec.engagement_snapshot);
				if (snapshot && snapshot.name) {
					snapshotName = snapshot.name;
				}
			}
		} catch (e) {
			console.warn("Unable to parse engagement_snapshot", e);
		}
		return snapshotName;
	};

	onSearch = (e) => {
		this.setState({ search: e.target.value });
	};

	renderRow = (rec) => {
		const { expandedId } = this.state;

		const snapshotName = this.getSnapshotName(rec);

		//compute next 5 upcoming dates for this recurring engagement
		const nextOccurrences = rec.rrule && rec.rrule.length > 0 ? this.getNextOccurrences(rec.rrule, 5) : [];

		const isExpanded = expandedId === rec.id;

		return (
			<div key={rec.id} className="rec-schedule__row">
				{/* Top row: only name + chevron */}
				<div className="rec-schedule__row-header" onClick={() => this.toggleExpand(rec.id)}>
					<div className="rec-schedule__row-title">
						<Icon.ChevronRight size={16} className={`rec-schedule__row-chevron ${isExpanded ? "rec-schedule__row-chevron--open" : ""}`} />
						<span className="rec-schedule__row-name">{snapshotName}</span>
					</div>
				</div>

				{/* Expanded details */}
				{isExpanded && (
					<div className="rec-schedule__row-details">
						<div className="rec-schedule__row-details-inner">
							{/* no raw RRULE, show next 5 appointment dates here */}
							<div className="rec-schedule__rrule">
								<div className="rec-schedule__next-header">
									<Icon.Calendar size={16} className="rec-schedule__next-header-icon" />
									<span>Next 5 appointments</span>
								</div>

								{nextOccurrences.length === 0 ? (
									<div className="rec-schedule__next-empty">No upcoming appointments</div>
								) : (
									<div className="rec-schedule__next-list">
										{nextOccurrences.map((label, index) => {
											// label is like "Wed, Nov 19, 2025"
											const [dayName, ...rest] = label.split(", ");
											const datePart = rest.join(", ");

											return (
												<div key={label} className={"rec-schedule__next-item" + (index === 0 ? " rec-schedule__next-item--primary" : "")}>
													<div className="rec-schedule__next-item-tag">{index === 0 ? "Next" : "Upcoming"}</div>
													<div className="rec-schedule__next-item-main">
														<div className="rec-schedule__next-item-day">{dayName}</div>
														<div className="rec-schedule__next-item-date">{datePart}</div>
													</div>
												</div>
											);
										})}
									</div>
								)}
							</div>

							{rec.engagement && (
								<div className="rec-schedule__meta">
									<div>
										<strong>Client:</strong> {rec.engagement.Client?.name || "—"}
									</div>
									<div>
										<strong>Team:</strong> {rec.engagement.Team?.name || "—"}
									</div>
									<div>
										<strong>Status:</strong> {rec.engagement.flow_state?.name || "—"}
									</div>
								</div>
							)}
						</div>
					</div>
				)}
			</div>
		);
	};

	render() {
		const { loading, recurringEngagements, search } = this.state;

		const filteredRecurringEngagements = recurringEngagements.filter((rec) => {
			const name = this.getSnapshotName(rec).toLowerCase();
			const term = (search || "").toLowerCase();
			if (!term) return true;
			return name.includes(term);
		});

		return (
			<div className="eng">
				<div className="eng__hd">
					<div className="eng__hd__title" style={{ display: "flex", alignItems: "center" }}>
						<div className="eng__hd__title__name">
							Recurring Engagement Schedule
						</div>

						<div style={{ marginLeft: "auto", maxWidth: 260 }}></div>
					</div>
				</div>

				<div className="engs__header">
					<div className="engs__header__actions">
						<SearchInput value={search} onChange={this.onSearch} placeholder="Search recurring..." />
					</div>
				</div>

				<div className="engagement">
					<div className="engagement__body">
						<div className="engagement__body__left rec-schedule__container">
							{loading && <p>Loading recurring engagements…</p>}

							{!loading && filteredRecurringEngagements.length === 0 && <p>No recurring engagements found.</p>}

							{!loading && filteredRecurringEngagements.length > 0 && filteredRecurringEngagements.map(this.renderRow)}
						</div>
					</div>
				</div>
			</div>
		);
	}
}

export default RecurringEngagementSchedule;
